import * as React from 'react'
import { useTranslation } from 'react-i18next'

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Select,
} from '@/components/ui'

import { useGetVersionList } from '../hooks'
import { useVersionComparisonStore } from '../store'

const VERSION_NUMBER_DECIMAL_PLACES = 1

const VersionSelectionDialog: React.FC = () => {
  const { t } = useTranslation()
  const {
    versionSelectionDialogOpen,
    documentId,
    documentFilename,
    setVersionSelectionDialogOpen,
    setCompareVersionDocument,
    setCompareVersions,
  } = useVersionComparisonStore()

  const { versions } = useGetVersionList({ documentId })

  // Get default values: previous version for source, current version for target
  const defaultSourceVersionId = React.useMemo(() => {
    // If there are at least 2 versions, use the second one (previous)
    // Otherwise, use the first one (current) if only one version exists
    return versions.length >= 2 ? versions[1].id : versions[0]?.id || ''
  }, [versions])

  const defaultTargetVersionId = React.useMemo(() => {
    // Current version is always the first one (highest version_number)
    return versions[0]?.id || ''
  }, [versions])

  // State for selected versions
  const [sourceVersionId, setSourceVersionId] = React.useState<string>(
    defaultSourceVersionId
  )
  const [targetVersionId, setTargetVersionId] = React.useState<string>(
    defaultTargetVersionId
  )

  // Update state when defaults change (when dialog opens with new document)
  React.useEffect(() => {
    setSourceVersionId(defaultSourceVersionId)
    setTargetVersionId(defaultTargetVersionId)
  }, [defaultSourceVersionId, defaultTargetVersionId])

  // Check if both versions are selected and different
  const isCompareEnabled = React.useMemo(() => {
    return (
      !!sourceVersionId &&
      !!targetVersionId &&
      sourceVersionId !== targetVersionId
    )
  }, [sourceVersionId, targetVersionId])

  // Check if versions are the same (for error message)
  const showSameVersionError = React.useMemo(() => {
    return (
      !!sourceVersionId &&
      !!targetVersionId &&
      sourceVersionId === targetVersionId
    )
  }, [sourceVersionId, targetVersionId])

  // Create options for Select components
  const versionOptions = React.useMemo(() => {
    return versions.map((version) => ({
      value: version.id,
      label: t('versionComparison.versionSelection.version', {
        version: version.version_number.toFixed(VERSION_NUMBER_DECIMAL_PLACES),
      }),
    }))
  }, [versions, t])

  const sourceVersionOption = React.useMemo(
    () => versionOptions.find((v) => v.value === sourceVersionId),
    [versionOptions, sourceVersionId]
  )

  const targetVersionOption = React.useMemo(
    () => versionOptions.find((v) => v.value === targetVersionId),
    [versionOptions, targetVersionId]
  )

  // Handle compare button click
  const handleCompare = React.useCallback(() => {
    if (!isCompareEnabled || !documentId) return

    // Set compare versions
    setCompareVersions([
      {
        id: sourceVersionId,
        label: sourceVersionOption?.label || '',
        isSource: true,
      },
      {
        id: targetVersionId,
        label: targetVersionOption?.label || '',
        isTarget: true,
      },
    ])
    // Close dialog and open comparison layout dialog
    setVersionSelectionDialogOpen(false)
  }, [
    documentId,
    isCompareEnabled,
    targetVersionId,
    targetVersionOption?.label,
    sourceVersionId,
    sourceVersionOption?.label,
    setCompareVersions,
    setVersionSelectionDialogOpen,
  ])

  return (
    <Dialog
      open={versionSelectionDialogOpen}
      onOpenChange={() => setCompareVersionDocument()}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="m-0 text-primary-900 text-lg font-semibold">
            {t('versionComparison.dialog.compareVersions')}
          </DialogTitle>
          <DialogDescription className="!m-0 font-medium text-placeholder">
            {t('versionComparison.dialog.fileCompare')}
            <span className="text-tertiary-600 font-semibold">
              {` “${documentFilename}” `}
            </span>
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            handleCompare()
          }}
          className="flex flex-col gap-4"
        >
          {/* Source Version Select */}
          <div className="flex flex-col gap-2">
            <Select
              label={t('versionComparison.versionSelection.sourceVersion')}
              options={versionOptions}
              value={sourceVersionId}
              onChange={setSourceVersionId}
              placeholder={t(
                'versionComparison.versionSelection.sourceVersion'
              )}
              fullWidth
              disableSorting
            />
          </div>

          {/* Target Version Select */}
          <div className="flex flex-col gap-2">
            <Select
              label={t('versionComparison.versionSelection.targetVersion')}
              options={versionOptions}
              value={targetVersionId}
              onChange={setTargetVersionId}
              placeholder={t(
                'versionComparison.versionSelection.targetVersion'
              )}
              fullWidth
              disableSorting
            />
          </div>

          {/* Error Message */}
          {showSameVersionError && (
            <div className="text-sm text-red-600">
              {t(
                'versionComparison.versionSelection.pleaseSelectDifferentVersions'
              )}
            </div>
          )}

          {/* Compare Button */}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outlined"
              color="default"
              onClick={() => setCompareVersionDocument()}
            >
              {t('versionComparison.versionSelection.cancel')}
            </Button>
            <Button
              type="submit"
              variant="solid"
              color="primary"
              disabled={!isCompareEnabled}
            >
              {t('versionComparison.versionSelection.compare')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

const VersionSelectionDialogMemoized = React.memo(VersionSelectionDialog)
VersionSelectionDialogMemoized.displayName = 'VersionSelectionDialog'

export { VersionSelectionDialogMemoized as VersionSelectionDialog }
